import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from 'src/common/utils/api';
import { API_URL, fetchWrapper } from 'src/common/utils/api/fetchWrapper';
import type {
  AutoBrandExtractResponse,
  AutoBrandExtractStartResponse,
  AutoBrandSourceKind,
} from 'src/secret-menu/auto-brand/types';
import type { ExportBrandingConfigData } from 'src/secret-menu/export-assets/types';

const POLLING_INTERVAL_MS = 2000;

type ExtractVars = { file: File; kind: AutoBrandSourceKind; ignoreEmbeddedImages?: boolean };

type BackgroundProcessStatus = 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';

interface BackgroundProcessResponse {
  id: string;
  status: BackgroundProcessStatus;
  statusMessage: string;
  extraDetails: Record<string, string> | null;
}

const isTerminalStatus = (status: BackgroundProcessStatus) =>
  status === 'SUCCESS' || status === 'FAILED';

export const useAutoBrandExtract = (companyId: string) => {
  const queryClient = useQueryClient();
  const [taskId, setTaskId] = useState<string | null>(null);

  const { mutateAsync: startExtract, isPending: isStarting } = useMutation<
    AutoBrandExtractStartResponse,
    Error,
    ExtractVars
  >({
    mutationFn: async ({ file, kind, ignoreEmbeddedImages = false }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kind', kind);
      formData.append('ignore_embedded_images', String(ignoreEmbeddedImages));
      const path = `/admin/companies/${companyId}/auto-brand/extract`;
      const { data } = await fetchWrapper(
        `${API_URL}${path}`,
        { method: 'POST', body: formData },
        `POST ${path}`,
      );
      return data as AutoBrandExtractStartResponse;
    },
    onSuccess: ({ taskId: newTaskId }) => {
      setTaskId(newTaskId);
    },
  });

  const { data: processStatus } = useQuery<BackgroundProcessResponse>({
    queryKey: ['admin', 'companies', companyId, 'background-processes', taskId],
    queryFn: async () => {
      const { data } = await api.get<BackgroundProcessResponse>(
        `admin/companies/${companyId}/background-processes/${taskId}`,
      );
      return data;
    },
    enabled: !!taskId,
    refetchInterval: query =>
      query.state.data && isTerminalStatus(query.state.data.status) ? false : POLLING_INTERVAL_MS,
  });

  const result: AutoBrandExtractResponse | null =
    processStatus?.status === 'SUCCESS' && processStatus.extraDetails
      ? {
          documentType: processStatus.extraDetails
            .document_type as AutoBrandExtractResponse['documentType'],
          config: JSON.parse(processStatus.extraDetails.config) as ExportBrandingConfigData,
        }
      : null;

  const error: Error | null =
    processStatus?.status === 'FAILED'
      ? new Error(processStatus.statusMessage || 'Branding extraction failed')
      : null;

  const isPolling = !!taskId && (!processStatus || !isTerminalStatus(processStatus.status));
  const isPending = isStarting || isPolling;

  const reset = () => {
    if (taskId) {
      queryClient.removeQueries({
        queryKey: ['admin', 'companies', companyId, 'background-processes', taskId],
      });
    }
    setTaskId(null);
  };

  return { extract: startExtract, isPending, result, error, reset };
};

export const useAutoBrandTitlePageFromImage = (companyId: string) => {
  const { mutateAsync, isPending, error } = useMutation<{ location: string }, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const path = `/admin/companies/${companyId}/auto-brand/title-page/from-image`;
      const { data } = await fetchWrapper(
        `${API_URL}${path}`,
        { method: 'POST', body: formData },
        `POST ${path}`,
        { timeoutMs: 120_000 },
      );
      return data as { location: string };
    },
  });

  return { buildFromImage: mutateAsync, isPending, error };
};
